import React, { useState, useEffect } from 'react'
import { Card } from "../../ui/card"
import { Skeleton } from "../../ui/skeleton"
import { MessageCircle, Sparkles, RefreshCw } from 'lucide-react'

export function HowItWorks() {
  const [isLoading, setIsLoading] = useState(true)
  
  const steps = [
    { number: "01", icon: MessageCircle, title: "Login & Open Chat — Context is ready", description: "Sign in once with your work email via Google. From then on, context is ready instantly." },
    { number: "02", icon: Sparkles, title: "Ask AIRA — From strategy to scripts", description: "Request anything. AIRA adapts responses to your needs." },
    { number: "03", icon: RefreshCw, title: "Continue Conversation — AIRA remembers", description: "Build on previous chats. Context carries across sessions." },
  ]

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 1400)
    return () => clearTimeout(timer)
  }, [])

  return (
    <section id="how-it-works" className="py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/30 via-purple-50/20 to-pink-50/30 dark:from-slate-900/50 dark:via-slate-800/30 dark:to-slate-900/50"></div>
      <div className="container mx-auto max-w-[1200px] px-10 md:px-[72px] relative z-10">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            How <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">It Works</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Three simple steps to unlock the full potential of AI-powered assistance.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
          {isLoading ? (
            // Skeleton loading state
            Array.from({ length: 3 }).map((_, index) => (
              <Card
                key={index}
                className="rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl ring-1 ring-slate-900/10 dark:ring-white/10 shadow-sm p-5 md:p-6 relative transition min-h-[200px] md:min-h-[220px]"
              >
                <div className="mb-2">
                  <Skeleton className="h-6 w-20 rounded-full" />
                </div>
                <div className="flex items-baseline gap-3 mb-2">
                  <Skeleton className="size-8 rounded-lg" />
                  <Skeleton className="h-6 w-48" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </Card>
            ))
          ) : (
            steps.map((step, index) => {
              const Icon = step.icon
              return (
                <Card
                  key={index}
                  className="rounded-2xl bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl ring-1 ring-slate-900/20 dark:ring-white/20 shadow-md hover:scale-105 relative transition min-h-[200px] md:min-h-[220px] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-400 group after:absolute after:inset-0 after:rounded-2xl after:opacity-100 after:transition after:bg-[radial-gradient(120px_120px_at_var(--mx,_70%)_0%,rgba(124,77,255,0.1),transparent_60%)] p-5 md:p-6"
                  tabIndex={0}
                  style={{ '--mx': '70%' } as React.CSSProperties}
                >
                  <div className="mb-2">
                    <div className="h-6 rounded-full px-2.5 text-[11px] font-medium tracking-wide text-muted-foreground bg-white/60 dark:bg-white/10 ring-1 ring-white/15 inline-flex items-center">
                      STEP {step.number}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-3 mb-2">
                    <div className="size-8 rounded-lg bg-white/60 dark:bg-white/10 ring-1 ring-white/15 grid place-items-center flex-shrink-0">
                      <Icon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                  <h3 className="text-[20px] leading-7 font-semibold  max-w-[28ch] text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-700 dark:group-hover:text-indigo-300 transition-colors">
                    {step.title}
                  </h3>
                  </div>
                  <p className="text-sm leading-6  max-w-[45ch] text-slate-600 dark:text-slate-400">
                    {step.description}
                  </p>
                </Card>
              )
            })
          )}
        </div>
      </div>
    </section>
  )
}


